import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, setBearerToken } from "@/lib/api";
import { applyUserLanguage, getStoredLanguage } from "@/i18n/translator";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authConfig, setAuthConfig] = useState({ mode: "emergent", google_client_id: "" });

  const checkAuth = useCallback(async () => {
    try {
      const r = await api.get("/auth/me");
      setUser(r.data);
      applyUserLanguage(r.data?.lang || getStoredLanguage());
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch the auth mode (emergent | google) from backend so the Login page knows which UI to show
  useEffect(() => {
    api.get("/auth/config").then((r) => setAuthConfig(r.data)).catch(() => { /* fallback to default */ });
  }, []);

  useEffect(() => {
    // Applique la langue mémorisée dès le chargement (page de login incluse)
    applyUserLanguage(getStoredLanguage());
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (_) {}
    setBearerToken(null);
    setUser(null);
    window.location.href = "/login";
  };

  const changeLang = async (lang) => {
    setUser((u) => (u ? { ...u, lang } : u));
    try { await api.put("/auth/me/lang", { lang }); } catch (_) {}
    applyUserLanguage(lang);
  };

  const isAdmin = (user?.role || "admin") === "admin";

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, logout, refresh: checkAuth, authConfig, isAdmin, changeLang }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
